<template>
  <div>
    <a-form
      ref="formRef"
      class="form selectors"
      :model="formState"
      :rules="rules"
      layout="vertical"
    >
      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity">
        <entity-picker :value="formState.entity" @update:value="formState.entity = $event" />
      </a-form-item>
      <a-form-item label="Campaña" name="campaign">
        <campaign-picker
          :showAll="false"
          :value="formState.campaign"
          @update:value="formState.campaign = $event"
          :entity="formState.entity"
        />
      </a-form-item>
      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base">
        <bases-picker
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.campaign"
        />
      </a-form-item>
      <a-form-item label="Fecha de inicio" name="startValue">
        <a-date-picker
          v-model:value="formState.startValue"
          :disabled-date="disableStartDate"
          format="DD/MM/YYYY"
          placeholder="-- / -- / ----"
          @openChange="startOpenChange"
        />
      </a-form-item>
      <a-form-item label="Fecha de fin" name="endValue">
        <a-date-picker
          v-model:value="formState.endValue"
          :disabled-date="disableEndDate"
          format="DD/MM/YYYY"
          placeholder="-- / -- / ----"
          :open="endOpen"
          @openChange="endOpenChange"
        />
      </a-form-item>
      <div v-if="!user?.customer?.type_business == 1"></div>
      <div v-if="!user?.customer?.enable_bases"></div>
      <div></div>
      <div class="back">
        <span v-if="total > -1" class="total">{{ total }} Registros</span>
        <div v-else></div>
        <a-tooltip placement="top">
          <template #title>
            <span v-if="isGlobalDownloading"> Tiene una descarga pendiente </span>
            <span v-else> Los datos se descargarán de acuerdo a los filtros seleccionados. </span>
          </template>
          <a-button
            type="link"
            :disabled="disabledButton || isGlobalDownloading"
            :loading="isLoading"
            @click="handleDownload"
          >
            Descargar
            <DownloadOutlined />
          </a-button>
        </a-tooltip>
        <a-button type="primary" :disabled="disabledButton" @click="handleSearch">
          Buscar
          <SearchOutlined />
        </a-button>
      </div>
    </a-form>
    <div>
      <a-table
        size="middle"
        :columns="columnsTable"
        :data-source="reportManagement?.items"
        :scroll="scrollStyle"
        :pagination="isPagination ? reportManagement?.pagination : undefined"
        @change="handleTableChange"
      >
        <template #annex="{ text }">
          <span>{{ text.split("@")[0] }}</span>
        </template>

        <template #date="{ text }">
          {{ dateTimeToSlashFormat(text, false) }}
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, reactive, ref, toRaw, watch } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";

import { downloadReportTypifications } from "@/app/reports/services";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { columnsFixedTableLeft } from "@/app/shared/utils/columnTable";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";

const columnsManagement = [
  {
    title: "Fecha",
    dataIndex: "date_management",
    key: "date_management",
    slots: { customRender: "date" },
    width: 120,
  },
  {
    title: "Usuario",
    dataIndex: "user_full_name",
    key: "user_full_name",
    width: 250,
  },
  {
    title: "Anexo",
    dataIndex: "annex",
    key: "annex",
    slots: { customRender: "annex" },
    width: 80,
  },
  {
    title: "Total de gestiones",
    dataIndex: "total_managements",
    key: "total_managements",
    width: 150,
  },
  {
    title: "Contacto directo",
    dataIndex: "direct_contact",
    key: "direct_contact",
    width: 150,
  },
  {
    title: "Contacto indirecto",
    dataIndex: "indirect_contact",
    key: "indirect_contact",
    width: 150,
  },
  {
    title: "No contacto",
    dataIndex: "no_contact",
    key: "no_contact",
    width: 150,
  },
  {
    title: "Cantidad de PDP",
    dataIndex: "total_pdp",
    key: "total_pdp",
    width: 150,
  },
  {
    title: "Monto de PDP",
    dataIndex: "amount_pdp",
    key: "amount_pdp",
    width: 150,
  },
  {
    title: "Campaña",
    dataIndex: "portfolio_name",
    key: "portfolio_name",
    width: 150,
  },
];

export default defineComponent({
  components: {
    EntityPicker,
    CampaignPicker,
    BasesPicker,
    DownloadOutlined,
    SearchOutlined,
  },
  setup() {
    const store = useStore();
    const user = computed(() => store.getters["getDataUser"]);
    const endOpen = ref(false);
    const isLoading = ref(false);
    const isPagination = ref(false);
    const formRef = ref();
    const formState = reactive({
      campaign: null,
      startValue: null,
      endValue: null,
    });
    const rules = {
      entity: [{ required: user.value?.customer?.type_business == 1, message: "Campo requerido" }],
      campaign: [{ required: true, message: "Campo requerido" }],
      base: [{ required: user.value?.customer?.enable_bases, message: "Campo requerido" }],
      startValue: [{ required: true, message: "Campo requerido" }],
      endValue: [{ required: true, message: "Campo requerido" }],
    };

    const disabledButton = computed(() => {
      for (const key in formState) {
        if (formState[key] === null) return true;
      }

      return false;
    });

    const reportManagement = computed(() => store.getters["getReportManagement"]);
    const total = computed(() => reportManagement.value?.pagination?.total);
    const columnsTable = computed(() => columnsFixedTableLeft(columnsManagement));

    const { scrollStyle } = useScrollResize({ headerMargin: 420 });
    const { isGlobalDownloading } = useGlobalDownloadState();

    // const onResize = () => {
    //   scrollStyle.value = { x: 1800, y: window.innerHeight - 420 };
    // };

    onMounted(() => {
      formRef.value.resetFields();
      store.commit("SET_COMMON_REPORTS", []);
      isPagination.value = false;
    });

    const disableStartDate = (startValue) => {
      if (!startValue || !formState.endValue) {
        return false;
      }

      return startValue.valueOf() > formState.endValue.valueOf();
    };

    const disableEndDate = (endValue) => {
      if (!endValue || !formState.startValue) {
        return false;
      }

      return formState.startValue.valueOf() >= endValue.valueOf();
    };

    const startOpenChange = (open) => {
      endOpen.value = !open;
    };

    const endOpenChange = (open) => {
      endOpen.value = open;
    };

    const handleDownload = async () => {
      try {
        isLoading.value = true;
        await downloadReportTypifications({
          entity: toRaw(formState).entity,
          campaign: toRaw(formState).campaign,
          base: toRaw(formState).base,
          startValue: formState.startValue.format("YYYY-MM-DD"),
          endValue: formState.endValue.format("YYYY-MM-DD"),
          type: 2,
        });
        isLoading.value = false;
      } catch (error) {
        console.log("Error");
        message.error("Ha ocurrido un error");
      }
    };

    const handleTableChange = (pagination) => {
      store.dispatch("fetchReportManagementProgress", {
        query: {
          entity: toRaw(formState).entity,
          campaign: toRaw(formState).campaign,
          base: toRaw(formState).base,
          startValue: formState.startValue.format("YYYY-MM-DD"),
          endValue: formState.endValue.format("YYYY-MM-DD"),
        },
        pagination: pagination,
      });
    };

    watch(user, (newValue) => {
      if (newValue.customer.type_business == 2) {
        formState.entity.value = 1;
      }
    });

    const handleSearch = async () => {
      await store.dispatch("fetchReportManagementProgress", {
        query: {
          entity: toRaw(formState).entity,
          campaign: toRaw(formState).campaign,
          base: toRaw(formState).base,
          startValue: formState.startValue.format("YYYY-MM-DD"),
          endValue: formState.endValue.format("YYYY-MM-DD"),
        },
        pagination: {},
      });
      isPagination.value = true;
    };

    return {
      formRef,
      formState,
      rules,
      user,
      endOpen,
      disableStartDate,
      disableEndDate,
      startOpenChange,
      endOpenChange,
      handleTableChange,
      dateTimeToSlashFormat,
      disabledButton,
      reportManagement,
      total,
      handleDownload,
      columnsTable,
      handleSearch,
      scrollStyle,
      isLoading,
      isPagination,
      isGlobalDownloading,
    };
  },
});
</script>

<style lang="sass" scoped>
.selectors
  display: grid
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 0.5fr 2fr
  gap: 15px
  padding: 15px 5px 5px
  margin-bottom: 10px
:deep(.ant-calendar-picker-icon)
  top: 50% !important
:deep(.ant-calendar-picker)
  width: 100%
.ant-form-item
  margin-bottom: 0
.back
  align-self: end
  text-align: right
.main-text
  font-size: 14px
  font-style: normal
  font-weight: 400
  line-height: 28px
  letter-spacing: 0em
  text-align: center
  color: $gray-7
.total
  margin-top: 2%
  color: $gray-8
</style>
